import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Label } from '@/app/components/ui/label';
import { LocationSearchComboBox } from '@/components/shared/location-search-combo-box';
import type { LocationOption } from '@/components/shared/location-search-combo-box';
import { JobSearchComboBox } from '@/components/shared/job-search-combo-box';
import type { JobOption } from '@/components/shared/job-search-combo-box';
import { useCreateToolboxTalk } from '@/hooks/use-safety';
import { useOrgToday } from '@/hooks/use-settings';
import { PersonSearchComboBox, type PersonOption } from '@/components/shared/person-search-combo-box';
import { OfflineIndicator } from '@/app/components/offline/offline-indicator';

interface TopicRow {
  topic: string;
  description: string;
}

interface AttendeeRow {
  tech_id: number | null;
  attendee_name: string;
  attendee_email: string;
  signature_data: string;
}

export function CreateToolboxTalkPage() {
  const navigate = useNavigate();
  const createMutation = useCreateToolboxTalk();
  const orgToday = useOrgToday();

  const [title, setTitle] = useState('');
  const [date, setDate] = useState(orgToday);
  const [selectedLocation, setSelectedLocation] = useState<LocationOption | null>(null);
  const [location, setLocation] = useState('');
  const [selectedProject, setSelectedProject] = useState<JobOption | null>(null);
  const [notes, setNotes] = useState('');
  const [topics, setTopics] = useState<TopicRow[]>([{ topic: '', description: '' }]);
  const [attendees, setAttendees] = useState<AttendeeRow[]>([]);

  const addTopic = () => {
    setTopics([...topics, { topic: '', description: '' }]);
  };

  const removeTopic = (index: number) => {
    setTopics(topics.filter((_, i) => i !== index));
  };

  const updateTopic = (index: number, field: keyof TopicRow, value: string) => {
    const updated = [...topics];
    updated[index] = { ...updated[index], [field]: value };
    setTopics(updated);
  };

  const addAttendee = () => {
    setAttendees([...attendees, { tech_id: null, attendee_name: '', attendee_email: '', signature_data: '' }]);
  };

  const removeAttendee = (index: number) => {
    setAttendees(attendees.filter((_, i) => i !== index));
  };

  const updateAttendee = (index: number, field: keyof AttendeeRow, value: string) => {
    const updated = [...attendees];
    updated[index] = { ...updated[index], [field]: value };
    setAttendees(updated);
  };

  const updateAttendeePerson = (index: number, person: PersonOption | null) => {
    const updated = [...attendees];
    updated[index] = {
      ...updated[index],
      tech_id: person?.tech_id ?? null,
      attendee_name: person?.name ?? '',
    };
    setAttendees(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const talk = await createMutation.mutateAsync({
        title,
        date,
        location_fk: selectedLocation?.id || null,
        location,
        project: selectedProject?.id,
        notes,
        topics: topics
          .filter((t) => t.topic.trim())
          .map((t, i) => ({ topic: t.topic, description: t.description, order: i + 1 })),
        attendees: attendees
          .filter((a) => a.attendee_name.trim())
          .map((a) => ({
            tech: a.tech_id ?? undefined,
            attendee_name: a.attendee_name,
            attendee_email: a.attendee_email || undefined,
            signature_data: a.signature_data || undefined,
          })),
      });
      navigate(`/safety/toolbox-talks/${talk.id}`);
    } catch {
      // error handled by mutation
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/safety/toolbox-talks')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Toolbox Talk</h1>
          <p className="text-gray-600">Record a safety briefing</p>
        </div>
      </div>

      <OfflineIndicator />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Morning Safety Brief - East Face"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Location</Label>
                <LocationSearchComboBox value={selectedLocation} onChange={setSelectedLocation} />
              </div>
              <div>
                <Label htmlFor="location">Specific Area / Details</Label>
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g. East face, 3rd floor scaffolding"
                />
              </div>
            </div>
            <div>
              <Label>Project</Label>
              <JobSearchComboBox
                value={selectedProject}
                onChange={(job) => {
                  setSelectedProject(job);
                  if (job?.location_fk_display && !selectedLocation) {
                    setSelectedLocation({
                      id: job.location_fk_display.id,
                      name: job.location_fk_display.name,
                      type: job.location_fk_display.type,
                    });
                  }
                }}
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Topics Covered</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addTopic}>
              <Plus className="h-4 w-4 mr-1" />
              Add Topic
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {topics.map((topic, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">Topic #{index + 1}</span>
                  {topics.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTopic(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
                <div>
                  <Label>Title</Label>
                  <Input
                    value={topic.topic}
                    onChange={(e) => updateTopic(index, 'topic', e.target.value)}
                    placeholder="e.g., Fall protection procedures"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={topic.description}
                    onChange={(e) => updateTopic(index, 'description', e.target.value)}
                    rows={2}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Attendees</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addAttendee}>
              <Plus className="h-4 w-4 mr-1" />
              Add Attendee
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {attendees.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No attendees added yet. Click "Add Attendee" to begin.
              </p>
            ) : (
              attendees.map((attendee, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-sm">Attendee #{index + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeAttendee(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Name</Label>
                      <PersonSearchComboBox
                        value={
                          attendee.attendee_name
                            ? { tech_id: attendee.tech_id, person_id: null, name: attendee.attendee_name }
                            : null
                        }
                        onChange={(person) => updateAttendeePerson(index, person)}
                        endpoint="persons"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        value={attendee.attendee_email}
                        onChange={(e) => updateAttendee(index, 'attendee_email', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/safety/toolbox-talks')}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating...' : 'Create Toolbox Talk'}
          </Button>
        </div>
      </form>
    </div>
  );
}
